import React, { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { FuzzyActions } from 'actions';

import Select from './Select';

const propTypes = {
    autoFocus: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    hint: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    onChange: PropTypes.func,
    value: PropTypes.any,
    className: PropTypes.string,
    error: PropTypes.any,
    mandatory: PropTypes.bool,
    readOnly: PropTypes.bool,
    hidden: PropTypes.bool,
    list: PropTypes.string,
    description: PropTypes.string,
    loadOptions: PropTypes.func,
    environmentField: PropTypes.object,
    idEnvironment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const getOptionId = (option) => {
    if (option && typeof option === 'object') return option.value ?? option.id ?? option.Id;
    return option;
};

const getFuzzyOptionValue = (option) => {
    const optionId = getOptionId(option);
    return optionId === null || optionId === undefined ? '' : String(optionId);
};

const isFuzzyOptionSelected = (option, selectedOptions = []) => {
    const optionId = getOptionId(option);
    if (optionId === null || optionId === undefined) return false;

    return selectedOptions.some(
        (selected) => getFuzzyOptionValue(selected) === getFuzzyOptionValue(option),
    );
};

const MultiSelectFuzzy = memo(
    ({
        shouldRenderField,
        hidden,
        options,
        list,
        onFetchOptions,
        field,
        feature,
        parentField,
        parentFieldForBackend,
        parentValue,
        isPureDependency,
        environmentField,
        idEnvironment,
        value,
        ...props
    }) => {
        const dispatch = useDispatch();

        const fetchOptions = useCallback(
            (text) => {
                if (onFetchOptions) {
                    return onFetchOptions({
                        list,
                        field,
                        text,
                        feature,
                    });
                } else {
                    // there are two types of dependency here... the ones standard for backend,
                    // who expect for parentField and parentValue (and an extrafieldunit in
                    // whereInfo field of the final XML), and the new available dependency
                    // sending to backend an "IdParent" field inside the same whereInfo
                    // used to filter by the dependant field, which seems that is calculated
                    // by backend in time of processing the request
                    let finalParentField = parentFieldForBackend || parentField;
                    let finalParentValue = parentValue;
                    let finalIdParent;
                    if (isPureDependency) {
                        finalIdParent = parentValue?.value || parentValue;
                        finalParentField = null;
                        finalParentValue = null;
                    }

                    // Normalize environmentId - could be object { value, label } or primitive
                    const envValue = environmentField?.value;
                    const envId =
                        envValue && typeof envValue === 'object'
                            ? envValue.value || envValue.id
                            : envValue;

                    return dispatch(
                        FuzzyActions.getFuzzy({
                            list,
                            field,
                            text,
                            feature,
                            parentField: finalParentField,
                            parentValue: finalParentValue,
                            idParent: finalIdParent,
                            environmentId: envId || idEnvironment,
                        }),
                    );
                }
            },
            [
                dispatch,
                onFetchOptions,
                list,
                field,
                feature,
                parentField,
                parentFieldForBackend,
                parentValue,
                isPureDependency,
                environmentField,
                idEnvironment,
            ],
        );

        if (hidden) return null;
        if (shouldRenderField && !shouldRenderField(props?.fieldId, list)) return null;

        return (
            <Select
                className="fm-field-multi-server-list"
                loadOptions={list ? fetchOptions : null}
                options={options}
                list={list}
                value={value}
                alwaysLoadOnFocus
                {...props}
                parentValue={parentValue}
                isOptionSelected={isFuzzyOptionSelected}
                getOptionValue={getFuzzyOptionValue}
                isMulti
                isFuzzy
            />
        );
    },
);

MultiSelectFuzzy.displayName = 'MultiSelectFuzzy';

MultiSelectFuzzy.propTypes = propTypes;

export default MultiSelectFuzzy;
